/**
 * „+ Neues Packstück“ (ADR 0013 §7, ADR 0016) -- für jeden Benutzer, nur mit Netz.
 *
 * Typ, Kennung, Bezeichnung, Standort (ADR 0028). Man wählt den Typ, und die
 * nächste freie Kennung steht da (GB-08 → GB-09), die Bezeichnung dazu
 * („Gitterbox GB-09“). Beides bleibt änderbar. Eine THW-Inventarnummer ist
 * freiwillig; ohne sie dient die Kennung
 * als Nummer. Der Standort ist zugleich der Stammplatz, und dort wird das
 * Packstück gleich eingebucht.
 *
 * Danach bietet die Seite „Etikett drucken“ an -- und den Weg zurück, etwa
 * mit dem neuen Packstück als Ziel in „Wohin?“.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import {
  INVENTARNUMMER_MUSTER,
  KENNUNG_MUSTER,
  type Lagerort,
  type PackstueckAngelegt,
  type PackstueckTyp,
  type PackstueckTypListe,
} from "@thw/contracts";
import { anfrage } from "../api/klient.js";
import { useOnline } from "../api/online.js";
import { useBestand } from "../lokal/bestand.js";
import { artikelMitNummer, kennungFuerTyp, packstueckAnlegen } from "../lokal/neuesPackstueck.js";
import { ortDesArtikels, zielName } from "../lokal/verschieben.js";
import { Eingabe, Knopf, Ladeanzeige, Rueckmeldung } from "./bausteine.jsx";
import { Wohin } from "./Wohin.jsx";

export interface NeuesPackstueckEigenschaften {
  /** Wo es stehen soll -- vorbelegt, etwa der Behälter, von dem man kommt. */
  ort_id: string | null;
  /** Beschriftung des Knopfs nach dem Anlegen: „Als Ziel nehmen“, „Zum Packstück“. */
  weiter: string;
  aufFertig: (lagerort: Lagerort) => void;
  aufAbbruch: () => void;
}

export function NeuesPackstueck({
  ort_id,
  weiter,
  aufFertig,
  aufAbbruch,
}: NeuesPackstueckEigenschaften) {
  const bestand = useBestand();
  const online = useOnline();
  // Einmal beim Öffnen gerechnet: Die Vorschläge sollen nicht springen,
  // während man tippt -- auch nicht, wenn nebenher der Abgleich läuft.
  // Die Typen kommen vom Server -- angelegt wird ohnehin nur mit Netz.
  const typen = useQuery({
    queryKey: ["packstueck-typen"],
    queryFn: () => anfrage<PackstueckTypListe>("GET", "/api/core/packstueck-typen"),
    enabled: online,
  });
  const aktiveTypen = (typen.data?.packstueck_typen ?? []).filter((t) => t.aktiv);
  const [typ, setTyp] = useState<string | null | undefined>(undefined);
  const [kennung, setKennung] = useState("");
  const [bezeichnung, setBezeichnung] = useState("");
  const [bezeichnungSelbst, setBezeichnungSelbst] = useState(false);
  const [nummer, setNummer] = useState("");
  const [ort, setOrt] = useState<string | null>(ort_id);
  const [waehlen, setWaehlen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [angelegt, setAngelegt] = useState<PackstueckAngelegt | null>(null);

  if (bestand === undefined) return <Ladeanzeige was="Das Lager" />;
  const typWaehlen = (t: PackstueckTyp | null): void => {
    setTyp(t?.kuerzel ?? null);
    if (t === null) return;
    const neu = kennungFuerTyp(bestand, t.kuerzel);
    setKennung(neu);
    if (!bezeichnungSelbst) setBezeichnung(`${t.bezeichnung} ${neu}`);
  };
  // Einmal, sobald die Typen da sind: der erste ist vorgewählt.
  if (typ === undefined && aktiveTypen[0] !== undefined) {
    typWaehlen(aktiveTypen[0]);
    return null;
  }

  if (waehlen) {
    return (
      <Wohin
        titel={`Wo steht ${kennung === "" ? "das Packstück" : kennung}?`}
        zweck="standort"
        aufAbbruch={() => {
          setWaehlen(false);
        }}
        aufWahl={(z) => {
          setWaehlen(false);
          if (z.art === "lagerort") setOrt(z.id);
        }}
      />
    );
  }

  const standort = ort === null ? undefined : bestand.lagerorte.get(ort);
  const kennungFehlt = kennung.trim() === "";
  const kennungFalsch = !kennungFehlt && !KENNUNG_MUSTER.test(kennung);
  const vergeben =
    !kennungFehlt &&
    ([...bestand.lagerorte.values()].some((l) => l.kennung === kennung) ||
      (nummer.trim() === "" && artikelMitNummer(bestand, kennung) !== null));
  const nummerFalsch = nummer.trim() !== "" && !INVENTARNUMMER_MUSTER.test(nummer.trim());
  const vorhanden =
    nummerFalsch || nummer.trim() === "" ? null : artikelMitNummer(bestand, nummer.trim());
  const schonKiste = vorhanden === null ? null : ortDesArtikels(bestand, vorhanden.id);

  const kannAnlegen =
    online &&
    !laeuft &&
    !kennungFehlt &&
    !kennungFalsch &&
    !vergeben &&
    bezeichnung.trim() !== "" &&
    !nummerFalsch &&
    schonKiste === null &&
    standort !== undefined &&
    standort.aktiv;

  const anlegen = async (): Promise<void> => {
    if (standort === undefined) return;
    setLaeuft(true);
    setFehler(null);
    try {
      setAngelegt(
        await packstueckAnlegen({
          kennung,
          bezeichnung: bezeichnung.trim(),
          inventarnummer: nummer.trim() === "" ? null : nummer.trim(),
          packstueck_typ: typ ?? null,
          ort_id: standort.id,
        }),
      );
    } catch (f) {
      setFehler(f instanceof Error ? f.message : String(f));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Neues Packstück"
      className="fixed inset-0 z-30 overflow-y-auto bg-neutral-100 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50"
    >
      <header className="sticky top-0 z-10 flex items-center justify-between gap-2 bg-blue-800 px-4 py-2 text-white">
        <h2 className="text-lg font-bold">Neues Packstück</h2>
        <button
          type="button"
          aria-label="Schließen"
          onClick={aufAbbruch}
          className="flex min-h-12 min-w-12 items-center justify-center rounded-lg text-2xl font-bold active:bg-blue-900"
        >
          ×
        </button>
      </header>

      <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 pb-24">
        {angelegt !== null ? (
          <>
            <Rueckmeldung
              art="gut"
              kopf={`✓ ${angelegt.lagerort.kennung} angelegt`}
              zusatz={
                `Steht in ${zielName(bestand, { art: "lagerort", id: angelegt.lagerort.eltern_id ?? "" })}` +
                (angelegt.artikel.stammplatz_id === null
                  ? " · ohne Stammplatz"
                  : " · das ist sein Stammplatz") +
                ` · Nummer ${angelegt.artikel.inventarnummer}`
              }
            />
            <Knopf
              art="haupt"
              onClick={() => {
                aufFertig(angelegt.lagerort);
              }}
            >
              {weiter}
            </Knopf>
            <Link
              to={`/etiketten?lagerort=${angelegt.lagerort.id}`}
              className="flex min-h-12 items-center justify-center rounded-xl bg-neutral-200 px-5 font-semibold active:bg-neutral-300 dark:bg-neutral-700"
            >
              Etikett drucken
            </Link>
          </>
        ) : (
          <>
            {!online && (
              <Rueckmeldung
                art="still"
                kopf="Nur mit Netz"
                zusatz="Ein Packstück lässt sich nur mit Verbindung zum Server anlegen — sonst könnten zwei Handys dieselbe Kennung vergeben."
              />
            )}

            {aktiveTypen.length > 0 && (
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1 text-sm font-semibold">Typ</legend>
                <div className="flex flex-wrap gap-2">
                  {aktiveTypen.map((t) => (
                    <button
                      key={t.kuerzel}
                      type="button"
                      aria-pressed={typ === t.kuerzel}
                      onClick={() => {
                        typWaehlen(t);
                      }}
                      className="min-h-12 rounded-full bg-blue-100 px-4 font-semibold text-blue-950 active:bg-blue-200 aria-pressed:bg-blue-700 aria-pressed:text-white dark:bg-blue-950 dark:text-blue-100"
                    >
                      {`${t.kuerzel} · ${t.bezeichnung}`}
                    </button>
                  ))}
                  <button
                    type="button"
                    aria-pressed={typ === null}
                    onClick={() => {
                      typWaehlen(null);
                    }}
                    className="min-h-12 rounded-full bg-neutral-200 px-4 font-semibold active:bg-neutral-300 aria-pressed:bg-blue-700 aria-pressed:text-white dark:bg-neutral-700"
                  >
                    Ohne Typ
                  </button>
                </div>
              </fieldset>
            )}

            <section className="flex flex-col gap-2">
              <Eingabe
                beschriftung="Kennung"
                wert={kennung}
                aendern={(neu) => {
                  setKennung(neu.toUpperCase().replace(/\s+/g, ""));
                }}
                hinweis={
                  kennungFalsch
                    ? "Nur Großbuchstaben, Ziffern und Bindestrich, höchstens 16 Zeichen."
                    : vergeben
                      ? `${kennung} ist schon vergeben.`
                      : undefined
                }
              />
            </section>

            <Eingabe
              beschriftung="Bezeichnung"
              wert={bezeichnung}
              aendern={(neu) => {
                setBezeichnung(neu);
                setBezeichnungSelbst(true);
              }}
            />

            <Eingabe
              beschriftung="THW-Inventarnummer (freiwillig)"
              wert={nummer}
              aendern={(neu) => {
                setNummer(neu.toUpperCase());
              }}
              hinweis={
                nummerFalsch
                  ? "Keine THW-Inventarnummer (Muster 0141-900639)."
                  : schonKiste !== null
                    ? `Diese Nummer ist schon das Packstück ${schonKiste.kennung}.`
                    : vorhanden !== null
                      ? `Gibt es schon: ${vorhanden.bezeichnung} — wird zu diesem Packstück.`
                      : "Leer lassen, wenn die Kiste keine hat — dann dient die Kennung als Nummer."
              }
            />

            <section className="flex flex-col gap-2">
              <span className="text-sm font-semibold">Standort</span>
              {standort === undefined ? (
                <Knopf
                  onClick={() => {
                    setWaehlen(true);
                  }}
                >
                  Standort wählen
                </Knopf>
              ) : (
                <div className="flex items-center justify-between gap-2 rounded-xl bg-white p-3 dark:bg-neutral-800">
                  <span className="flex flex-col">
                    <span className="font-semibold">
                      {standort.standardziel
                        ? standort.bezeichnung
                        : `${standort.kennung} — ${standort.bezeichnung}`}
                    </span>
                    <span className="text-sm text-neutral-600 dark:text-neutral-300">
                      {standort.standardziel
                        ? "Hier bleibt es ohne Stammplatz."
                        : "Hier steht es, und hier ist sein Stammplatz."}
                    </span>
                  </span>
                  <Knopf
                    art="still"
                    onClick={() => {
                      setWaehlen(true);
                    }}
                  >
                    ändern
                  </Knopf>
                </div>
              )}
            </section>

            {fehler !== null && (
              <Rueckmeldung art="warnung" kopf="Nicht angelegt" zusatz={fehler} />
            )}

            <Knopf art="haupt" disabled={!kannAnlegen} onClick={() => void anlegen()}>
              {laeuft ? "Wird angelegt …" : `${kennung === "" ? "Packstück" : kennung} anlegen`}
            </Knopf>
          </>
        )}
      </div>
    </div>
  );
}
